import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { en, type MessageKey } from './messages';
import { dictionaryOf, useLanguage } from './locales/state';
import { dirOf, type Locale } from './locales/registry';

export type { MessageKey } from './messages';
export type { Locale } from './locales/registry';
export { LOCALES } from './locales/registry';
export type Vars = Record<string, string | number>;
type Namespace = Record<string, string> | undefined;

const plurals = new Map<string, Intl.PluralRules>();
const pluralOf = (l: string, n: number) => {
  let p = plurals.get(l);
  if (!p) plurals.set(l, (p = new Intl.PluralRules(l)));
  return p.select(n);
};

/** `k`, or its plural form for `count` in that language (`k_one`, `k_few`… then `k_other`). */
function pick(ns: Namespace, k: string, count: number | null, l: string): string | undefined {
  if (!ns) return undefined;
  if (count !== null) {
    const m = ns[`${k}_${pluralOf(l, count)}`] ?? ns[`${k}_other`];
    if (m !== undefined) return m;
  }
  return ns[k];
}

/**
 * Looks a key up and fills `{name}` placeholders: in the locale's dictionary first, then in
 * English (a locale without the key, or without copy at all, shows the English text). Plurals:
 * pass `count` in vars and define `key_one` / `key_other` (Intl.PluralRules categories of
 * each language); the bare key is the fallback. A key English doesn't have either renders the
 * key itself, so a gap is visible rather than blank.
 */
export function translate(locale: Locale, key: MessageKey | string, vars?: Vars): string {
  const dot = key.indexOf('.');
  const ns = key.slice(0, dot);
  const k = key.slice(dot + 1);
  const count = vars && typeof vars.count === 'number' ? vars.count : null;
  let msg = locale === 'en' ? undefined : pick(dictionaryOf(locale)?.[ns], k, count, locale);
  const rtl = locale !== 'en' && dirOf(locale) === 'rtl';
  // Right to left, "{from} → {to}" reads from the right: the arrow turns (the locale's own text only).
  if (rtl && msg) msg = msg.replace(/→/g, '←');
  msg ??= pick((en as unknown as Record<string, Namespace>)[ns], k, count, 'en');
  if (msg === undefined) {
    if (import.meta.env.DEV) console.warn(`[i18n] missing key ${key}`);
    return key;
  }
  // Right to left, each inserted value is isolated (FSI…PDI, as <bdi>): an email, a route, a
  // code or a name keeps its own direction and doesn't reorder the sentence around it.
  const fill = rtl ? (v: string | number) => `\u2068${v}\u2069` : String;
  return vars ? msg.replace(/\{(\w+)\}/g, (m, name: string) => (name in vars ? fill(vars[name]!) : m)) : msg;
}

export type T = (key: MessageKey, vars?: Vars) => string;

const I18nContext = createContext<{ locale: Locale; t: T }>({
  locale: 'en',
  t: (key, vars) => translate('en', key, vars),
});

/**
 * The copy's language for everything below: the visit's (locales/state.ts), or `locale` when
 * given (tests; its dictionary must be loaded, `addDictionary`).
 */
export function I18nProvider({ locale, children }: { locale?: Locale; children: ReactNode }) {
  const { text } = useLanguage();
  const l = locale ?? text;
  const value = useMemo(() => ({ locale: l, t: ((key, vars) => translate(l, key, vars)) as T }), [l]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

/** `const t = useT(); t('S1.title')` */
export function useT(): T {
  return useContext(I18nContext).t;
}

/** The language the copy is in (English until a locale's dictionary is in). */
export function useLocale(): Locale {
  return useContext(I18nContext).locale;
}
